import React, { useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Settings,
  Building,
  Wifi,
  Shield,
  Database,
  Save,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [showPasswords, setShowPasswords] = useState(false);
  const [formData, setFormData] = useState({
    businessName: 'Demo Business',
    businessAddress: 'Lilongwe, Malawi',
    businessPhone: '+265 123 456 789',
    businessEmail: 'info@demo.local',
    tpin: '100000000',
    mraTerminalId: 'TERM001',
    mraDeviceId: 'DEV001',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const tabs = [
    { id: 'general', name: 'General', icon: Settings },
    { id: 'business', name: 'Business', icon: Building },
    { id: 'mra', name: 'MRA Settings', icon: Wifi },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'database', name: 'Database', icon: Database },
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Save logic would go here
    toast.success('Settings saved successfully');
  };

  const handlePasswordChange = () => {
    // Password change logic would go here
    toast.success('Password change initiated');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Settings
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage your system configuration and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="card-body p-0">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        w-full flex items-center px-3 py-2 text-sm font-medium rounded-l transition-colors duration-200
                        ${activeTab === tab.id
                          ? 'bg-blue-100 text-blue-700 border-blue-200'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-transparent'
                        } border
                      `}
                    >
                      <Icon className="h-5 w-5 mr-3" />
                      {tab.name}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="card"
          >
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">
                {tabs.find(tab => tab.id === activeTab)?.name}
              </h3>
            </div>
            <div className="card-body space-y-6">
              {activeTab === 'general' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      System Language
                    </label>
                    <select className="input">
                      <option>English</option>
                      <option>Chichewa</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timezone
                    </label>
                    <select className="input">
                      <option>UTC+2 (Central Africa Time)</option>
                      <option>UTC+0 (GMT)</option>
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">
                        Enable email notifications
                      </span>
                    </label>
                  </div>
                </>
              )}

              {activeTab === 'business' && (
                <>
                  <Input
                    label="Business Name"
                    value={formData.businessName}
                    onChange={(e) => handleInputChange('businessName', e.target.value)}
                  />
                  <Input
                    label="Business Address"
                    value={formData.businessAddress}
                    onChange={(e) => handleInputChange('businessAddress', e.target.value)}
                  />
                  <Input
                    label="Business Phone"
                    value={formData.businessPhone}
                    onChange={(e) => handleInputChange('businessPhone', e.target.value)}
                  />
                  <Input
                    label="Business Email"
                    type="email"
                    value={formData.businessEmail}
                    onChange={(e) => handleInputChange('businessEmail', e.target.value)}
                  />
                </>
              )}

              {activeTab === 'mra' && (
                <>
                  <Input
                    label="TPIN"
                    value={formData.tpin}
                    onChange={(e) => handleInputChange('tpin', e.target.value)}
                    placeholder="Enter your Taxpayer Identification Number"
                  />
                  <Input
                    label="MRA Terminal ID"
                    value={formData.mraTerminalId}
                    onChange={(e) => handleInputChange('mraTerminalId', e.target.value)}
                  />
                  <Input
                    label="MRA Device ID"
                    value={formData.mraDeviceId}
                    onChange={(e) => handleInputChange('mraDeviceId', e.target.value)}
                  />
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Connection Status
                    </div>
                    <div className="flex items-center">
                      <div className="h-2 w-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-green-700">
                        Connected
                      </span>
                    </div>
                  </div>
                  <Button variant="secondary" className="w-full">
                    Test Connection
                  </Button>
                </>
              )}

              {activeTab === 'security' && (
                <>
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Change Password
                    </h4>
                    <Input
                      label="Current Password"
                      type={showPasswords ? 'text' : 'password'}
                      value={formData.currentPassword}
                      onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                    />
                    <Input
                      label="New Password"
                      type={showPasswords ? 'text' : 'password'}
                      value={formData.newPassword}
                      onChange={(e) => handleInputChange('newPassword', e.target.value)}
                    />
                    <Input
                      label="Confirm New Password"
                      type={showPasswords ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    />
                    <div className="flex items-center justify-between">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showPasswords}
                          onChange={(e) => setShowPasswords(e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">
                          Show passwords
                        </span>
                      </label>
                      <Button
                        onClick={handlePasswordChange}
                        disabled={!formData.currentPassword || !formData.newPassword}
                      >
                        Update Password
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'database' && (
                <>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          Database Backup
                        </h4>
                        <p className="text-xs text-gray-500">
                          Last backup: 2 hours ago
                        </p>
                      </div>
                      <Button variant="secondary">
                        Backup Now
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          Database Restore
                        </h4>
                        <p className="text-xs text-gray-500">
                          Restore from backup file
                        </p>
                      </div>
                      <Button variant="secondary">
                        Restore
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          Export Data
                        </h4>
                        <p className="text-xs text-gray-500">
                          Export sales and product data
                        </p>
                      </div>
                      <Button variant="secondary">
                        Export
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="card-footer">
              <Button onClick={handleSave} className="flex items-center">
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
